import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface Download {
  id: string;
  title: string;
  description: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  downloadCount: number;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  contratos: "Contratos",
  manuais: "Manuais",
  apresentacoes: "Apresentações",
  cases: "Cases",
  checklists: "Checklists",
  modelos: "Modelos",
};

const categoryColors: Record<string, string> = {
  contratos: "bg-accent-100 text-accent-800",
  manuais: "bg-green-100 text-green-800",
  apresentacoes: "bg-accent-100 text-accent-800",
  cases: "bg-orange-100 text-orange-800",
  checklists: "bg-yellow-100 text-yellow-800",
  modelos: "bg-gray-100 text-gray-800",
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export default function DownloadsPage() {
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/downloads")
      .then((json) => {
        if (json.success) {
          setDownloads(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(downloads.map((d) => d.category))];

  const filteredDownloads = selectedCategory
    ? downloads.filter((d) => d.category === selectedCategory)
    : downloads;

  const handleDownload = async (download: Download) => {
    try {
      await apiFetch(`/api/downloads/${download.id}/download`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Erro ao registrar download:", error);
    }

    window.open(download.fileUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium hover:text-accent-600">Chamados</a>
            <a href="/downloads" className="text-sm font-medium text-accent-600">Downloads</a>
            <a href="/licencas" className="text-sm font-medium hover:text-accent-600">Licenças</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Downloads</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Acesse materiais, manuais e documentos
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedCategory === null
                ? "bg-accent-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            Todos
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? "bg-accent-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              {categoryLabels[category] || category}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredDownloads.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhum download disponível nesta categoria.
              </p>
            </div>
          ) : (
            filteredDownloads.map((download) => (
              <div
                key={download.id}
                className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
              >
                <div className="flex items-start justify-between">
                  <Badge className={categoryColors[download.category] || "bg-gray-100 text-gray-800"}>
                    {categoryLabels[download.category] || download.category}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    {download.downloadCount} downloads
                  </span>
                </div>

                <h3 className="mt-4 text-lg font-semibold">{download.title}</h3>
                {download.description && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {download.description}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <span>{download.fileName}</span>
                  <span>{formatFileSize(download.fileSize)}</span>
                </div>

                <button
                  onClick={() => handleDownload(download)}
                  className="mt-4 w-full rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
                >
                  Download
                </button>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
